import React, { Component } from "react";
import { graphql } from "@apollo/client/react/hoc";
import compose from "lodash.flowright";
import PropTypes from "prop-types";
import { withTranslation } from "react-i18next";

import { GET_AUTH } from "../../../graphql/queries";
import { logError } from "../../../helpers/logging";
import { showSuccessAlert, showErrorAlert } from "../../../helpers/alerts";
import { I18N_NAMESPACES } from "../../../helpers/constants";

import Loading from "../../atoms/Loading";
import OffCanvasTray from "../OffCanvasTray";

import {
  StyledBoxTray,
  Wrapper,
  Option,
  AddToCartButton,
  Title,
  Subtitle,
  CloseButton,
  OptionWrapper,
} from "./style";

class SpecialPackAddToCartTray extends Component {
  static propTypes = {
    authQuery: PropTypes.shape({}).isRequired,
    onClose: PropTypes.func.isRequired,
    closeTrays: PropTypes.func.isRequired,
    specialPackEdition: PropTypes.shape({}).isRequired,
    // Option product to start at quantity 1, when opened from that option's own card
    preselectedProductId: PropTypes.number,
    t: PropTypes.func.isRequired,
  };

  static defaultProps = {
    preselectedProductId: null,
  };

  state = {
    items: [],
    addingItemsToCart: false,
    submitting: false,
  };

  static getDerivedStateFromProps(nextProps, previousState) {
    const {
      specialPackEdition: { specialpackoptionSet },
      preselectedProductId,
    } = nextProps;
    if (!previousState.items.length && specialpackoptionSet.length) {
      const isSingleOption = specialpackoptionSet.length === 1;
      return {
        items: specialpackoptionSet.map((item) => ({
          product: item.product,
          quantity: isSingleOption || item.product.id === preselectedProductId ? 1 : 0,
        })),
      };
    }
    return null;
  }

  updateSpecialPackOptionQuantity = (product, amount) => {
    this.setState((previousState) => {
      const specialPack = previousState.items.find((item) => item.product.id === product.id);
      if (!specialPack && amount > 0) {
        return {
          items: [...previousState.items, { product, quantity: amount }],
        };
      }
      const newItems = previousState.items
        .map((item) => {
          if (item.product.id === product.id) {
            return { product, quantity: item.quantity + amount };
          }
          return item;
        })
        .filter((item) => item.quantity >= 0);
      return { items: [...newItems] };
    });
  };

  removeSpecialPack = (product) => () => {
    this.updateSpecialPackOptionQuantity(product, -1);
  };

  addSpecialPack = (product) => () => {
    this.updateSpecialPackOptionQuantity(product, 1);
  };

  getOptionQuantity = (productId) => {
    const { items } = this.state;
    return (items.find((item) => item.product.id === productId) || { quantity: 0 }).quantity;
  };

  addToCartCallbackSuccess = (data = {}) => {
    const { closeTrays, t } = this.props;
    this.setState({ submitting: false });
    let errors;
    Object.keys(data).forEach((property) => {
      if (data[property].errors && data[property].errors.length) {
        // eslint-disable-next-line prefer-destructuring
        errors = data[property].errors;
      }
    });

    if (errors) {
      showErrorAlert(errors[0].messages[0]);
    } else {
      showSuccessAlert(t("specialPacks:specialPacksAdded", { count: this.getAmountSelected() }));
    }
    closeTrays();
  };

  addToCartCallbackFail = (error) => {
    const { onClose, t } = this.props;
    this.setState({ submitting: false });
    logError(error);
    showErrorAlert(t("common:sorryTryAgain"));
    onClose();
  };

  getAmountSelected = () => {
    const { items } = this.state;
    return items.reduce((acc, item) => acc + item.quantity, 0);
  };

  render() {
    const { onClose, specialPackEdition, authQuery, t } = this.props;
    const { addingItemsToCart, items, submitting } = this.state;
    const amountItemsSelected = this.getAmountSelected();
    if (authQuery.loading) {
      return <h1>{t("common:loading")}</h1>;
    }
    if (!specialPackEdition) {
      return <h1>{t("specialPacks:noInfo")}</h1>;
    }

    const { specialpackoptionSet } = specialPackEdition;
    const availableSpecialPackOptions =
      specialpackoptionSet &&
      specialpackoptionSet.filter((item) => item.product.isEnabled).sort((a, b) => a.sortOrder - b.sortOrder);
    const selectedItems = items.filter((item) => item.quantity > 0);
    return (
      <OffCanvasTray setOverflowHidden={false} isOffcanvasOpened outSideClickHandler={onClose}>
        <StyledBoxTray isOpened>
          <Wrapper>
            {addingItemsToCart && <Loading />}
            <CloseButton onClick={onClose} />
            <Title>{t("common:specialPacks")}</Title>
            <Subtitle>
              {t("specialPacks:numberBoxes")}
              <b>{` ${specialPackEdition.name}`}</b>.
            </Subtitle>
            <OptionWrapper>
              {availableSpecialPackOptions.map((option) => (
                <Option
                  key={option.product.id}
                  label={option.displayName}
                  quantity={this.getOptionQuantity(option.product.id)}
                  removeSpecialPack={this.removeSpecialPack(option.product)}
                  addSpecialPack={this.addSpecialPack(option.product)}
                />
              ))}
            </OptionWrapper>
            <AddToCartButton
              disabled={amountItemsSelected === 0 || submitting}
              onClick={() => this.setState({ addingItemsToCart: true, submitting: true })}
              callbackOnAddSuccess={this.addToCartCallbackSuccess}
              callbackOnAddFail={this.addToCartCallbackFail}
              products={selectedItems}
              label={t("specialPacks:addToCart")}
            />
          </Wrapper>
        </StyledBoxTray>
      </OffCanvasTray>
    );
  }
}

export default compose(
  graphql(GET_AUTH, { name: "authQuery" }),
  withTranslation([I18N_NAMESPACES.SPECIAL_PACKS, I18N_NAMESPACES.COMMON])
)(SpecialPackAddToCartTray);
